import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppPopupService } from 'src/app/shared/services/app-popup.service';
import { SurveysService } from 'src/app/shared/services/survey.service';
import { SurveyOfflineSyncService } from 'src/app/shared/services/survey-offline-sync.service';
import { NetworkConnectivityService } from 'src/app/shared/services/network-connectivity.service';
import {
  ServiceWorkerService,
  OfflineCacheStatus,
} from 'src/app/shared/services/service-worker.service';
import { environment } from 'src/environments/environment';
import { MessageModalComponent } from 'src/app/shared/components/message-modal/message-modal.component';
import { SyncReportModalComponent } from 'src/app/shared/components/sync-report-modal/sync-report-modal.component';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
})
export class SettingsComponent implements AfterViewInit, OnDestroy {
  isDeveloperMode = true;
  version = environment.version;
  env = environment.name;
  isLocalhost = false;
  isKioskMode = false;
  pendingSubmissionCount = 0;
  isSyncing = false;
  isMultiplying = false;
  pendingMultiplier = 20;
  lastSyncAt: number | null = null;
  isOnline = false;
  offlineCacheStatus: OfflineCacheStatus = 'not_registered';
  offlineCacheLabel = 'Not installed';
  private pendingCountSub?: Subscription;
  private lastSyncAtSub?: Subscription;
  private onlineStatusSub?: Subscription;
  private offlineCacheStatusSub?: Subscription;
  private offlineCacheLabelSub?: Subscription;

  constructor(
    private surveysService: SurveysService,
    private offlineSync: SurveyOfflineSyncService,
    private networkConnectivity: NetworkConnectivityService,
    private serviceWorkerService: ServiceWorkerService,
    private router: Router,
    private appPopup: AppPopupService,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    this.isOnline = this.networkConnectivity.isOnline;
    this.isLocalhost = this.isRunningOnLocalhost();
  }

  async ngAfterViewInit(): Promise<void> {
    const isKioskMode = localStorage.getItem('kioskMode');
    if (isKioskMode !== null) {
      const parsedValue = JSON.parse(isKioskMode);
      if (typeof parsedValue === 'boolean') {
        this.isKioskMode = parsedValue;
      }
    }

    this.pendingCountSub = this.offlineSync.pendingCount$.subscribe((count) => {
      this.pendingSubmissionCount = count;
      this.cdr.detectChanges();
    });

    this.lastSyncAt = this.offlineSync.lastSyncAt;
    this.lastSyncAtSub = this.offlineSync.lastSyncAt$.subscribe((timestamp) => {
      this.lastSyncAt = timestamp;
      this.cdr.detectChanges();
    });

    this.isOnline = this.networkConnectivity.isOnline;
    this.onlineStatusSub = this.networkConnectivity.isOnline$.subscribe(
      (online) => {
        this.isOnline = online;
        this.cdr.detectChanges();
      }
    );
    await this.networkConnectivity.refreshOnlineStatus();
    this.isOnline = this.networkConnectivity.isOnline;

    this.offlineCacheStatus = this.serviceWorkerService.status;
    this.offlineCacheLabel = this.serviceWorkerService.statusLabel;
    this.offlineCacheStatusSub = this.serviceWorkerService.status$.subscribe(
      (status) => {
        this.offlineCacheStatus = status;
        this.cdr.detectChanges();
      }
    );
    this.offlineCacheLabelSub =
      this.serviceWorkerService.statusLabel$.subscribe((label) => {
        this.offlineCacheLabel = label;
        this.cdr.detectChanges();
      });

    this.isLocalhost = this.isRunningOnLocalhost();
    this.cdr.detectChanges();
  }

  get lastSyncLabel(): string {
    if (!this.lastSyncAt) {
      return 'Never';
    }

    return new Date(this.lastSyncAt).toLocaleString();
  }

  ngOnDestroy(): void {
    this.pendingCountSub?.unsubscribe();
    this.lastSyncAtSub?.unsubscribe();
    this.onlineStatusSub?.unsubscribe();
    this.offlineCacheStatusSub?.unsubscribe();
    this.offlineCacheLabelSub?.unsubscribe();
  }

  clearData() {
    if (this.pendingSubmissionCount > 0) {
      const dialogRef = this.dialog.open(MessageModalComponent, {
        width: '280px',
        data: {
          text: 'There are pending submissions that have not synced yet. Clearing data will remove them. Continue?',
          type: 'warning',
          confirmText: 'Clear all',
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'confirm') {
          this.performClearData();
        }
      });
      return;
    }

    this.performClearData();
  }

  private performClearData() {
    this.surveysService
      .deleteAll()
      .then(() => {
        this.appPopup.info('Success', 'All data was deleted', 3000);
        this.cdr.detectChanges();
        this.goHome();
      })
      .catch((err) => {
        console.error('Error', err);
      });
  }

  async syncNow(): Promise<void> {
    await this.networkConnectivity.refreshOnlineStatus();
    this.isOnline = this.networkConnectivity.isOnline;
    this.cdr.detectChanges();

    if (!this.isOnline) {
      this.appPopup.warning(
        'Offline',
        'Connect to the internet to sync submissions.'
      );
      return;
    }

    this.isSyncing = true;
    this.cdr.detectChanges();

    this.offlineSync.markFlushRunning();
    this.dialog.open(SyncReportModalComponent, {
      width: '360px',
      disableClose: true,
      autoFocus: false,
    });

    try {
      await this.offlineSync.flushPendingSubmissions();
    } finally {
      this.isSyncing = false;
      this.cdr.detectChanges();
    }
  }

  get showTestMultiplier(): boolean {
    return environment.enableTestMultiplier === true || this.isLocalhost;
  }

  async multiplyPending(): Promise<void> {
    if (!this.showTestMultiplier || this.isSyncing || this.isMultiplying) {
      return;
    }
    if (this.pendingSubmissionCount === 0) {
      return;
    }

    this.isMultiplying = true;
    this.cdr.detectChanges();

    try {
      await this.offlineSync.multiplyPendingSubmissions(this.pendingMultiplier);
    } finally {
      this.isMultiplying = false;
      this.cdr.detectChanges();
    }
  }

  goHome() {
    this.router.navigate(['/public']);
  }

  private isRunningOnLocalhost(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const hostname = window.location.hostname;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]'
    );
  }

  toggleKioskMode(): void {
    this.isKioskMode = !this.isKioskMode;
    this.onKioskModeChange();
  }

  onKioskModeChange() {
    localStorage.setItem('kioskMode', JSON.stringify(this.isKioskMode));
    if (this.isKioskMode) {
      this.appPopup.warning('Kiosk-Mode', 'enabled');
    } else {
      this.appPopup.info('Kiosk-Mode', 'disabled');
    }
  }

  alertTest() {
    this.appPopup.info('Test', 'Testing popup', 1000);
  }
}
